import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Howl } from "howler";
import confetti from "canvas-confetti";
import useApiCallHooks from "../../../hooks/useApiCallHooks";
import { Link, useParams } from "react-router-dom";
// Sound setup
const soundEffects = {
    correct: new Howl({ src: ["/assets/sounds/correct.mp3"], volume: 0.8 }),
    wrong: new Howl({ src: ["/assets/sounds/wrong-buzzer.mp3"], volume: 0.8 }),
    click: new Howl({ src: ["/assets/sounds/button-click.mp3"], volume: 0.6 }),
    transition: new Howl({
        src: ["/assets/sounds/transition.mp3"],
        volume: 0.5,
    }),
    background: new Howl({
        src: ["/assets/sounds/Barnville.mp3"],
        loop: true,
        volume: 0.4,
        pool: true,
    }),
    result: new Howl({
        src: ["/assets/sounds/result-fanfare.mp3"],
        volume: 0.7,
    }),
};
// Questions
const questions = [
    {
        question: "💻 What does HTML stand for?",
        options: [
            "Hyper Text Markup Language",
            "High Tech Modern Language",
            "Hot Mail",
            "Home Tool Markup Language",
        ],
        answer: "Hyper Text Markup Language",
    },
    {
        question: "🧠 What is used to style a webpage?",
        options: ["HTML", "CSS", "Python", "SQL"],
        answer: "CSS",
    },
    {
        question: "🔢 What symbol is used to add numbers in JavaScript?",
        options: ["+", "-", "*", "/"],
        answer: "+",
    },
    {
        question: "🔄 What loop runs a block of code multiple times?",
        options: ["if", "for", "break", "alert"],
        answer: "for",
    },
    {
        question: "📦 What keyword declares a variable in JavaScript?",
        options: ["const", "print", "var", "function"],
        answer: "var",
    },
];

const QuizeGame = () => {
    const [responce, loading, error, callAPI, statusCode] = useApiCallHooks();

    const [gameStarted, setGameStarted] = useState(false);
    const [showStart, setShowStart] = useState(false);
    const [countdown, setCountdown] = useState(3);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [score, setScore] = useState(0);
    const [showResult, setShowResult] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [bgClass, setBgClass] = useState("techquiz-primary-bg");
    const [isOptionDisabled, setIsOptionDisabled] = useState(false);
    const [questions, setQuestions] = useState([]);
    const [bgMusicOn, setBgMusicOn] = useState(true);
    const [soundFxOn, setSoundFxOn] = useState(true);
    const { slug } = useParams();
    // Countdown effect

    useEffect(() => {
        callAPI("get", `quiz-details/${slug}`);
    }, [slug]);
    if (
        responce?.data &&
        questions.length === 0 &&
        responce.message === "QuizQuestions"
    ) {
        setQuestions(responce.data);
    }

    useEffect(() => {
        if (showStart && countdown > 0) {
            const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
            return () => clearTimeout(timer);
        } else if (countdown === 0) {
            setTimeout(() => setShowStart(false), 1000);
        }
    }, [countdown, showStart]);
    // Background music effect
    useEffect(() => {
        if (bgMusicOn) {
            soundEffects.background.play();
        } else {
            soundEffects.background.stop();
        }
        return () => {
            soundEffects.background.stop();
        };
    }, [bgMusicOn]);
    const handleAnswer = (selectedAnswer) => {
        if (isOptionDisabled) return;
        setIsOptionDisabled(true);
        if (soundFxOn) soundEffects.click.play();
        const isCorrect = selectedAnswer === questions[currentQuestion].answer;
        if (isCorrect) {
            if (soundFxOn) soundEffects.correct.play();
            setScore((prev) => prev + 1);
            setFeedback("🎉 Correct!");
            setBgClass("techquiz-correct-bg");
            confetti({ particleCount: 150, spread: 90, origin: { y: 0.6 } });
        } else {
            if (soundFxOn) soundEffects.wrong.play();
            setFeedback("❌ Oops! Try the next one!");
            setBgClass("techquiz-wrong-bg");
        }
        setTimeout(() => {
            if (soundFxOn) soundEffects.transition.play();
            setFeedback(null);
            setBgClass("techquiz-primary-bg");
            setIsOptionDisabled(false);
            if (currentQuestion + 1 < questions.length) {
                setCurrentQuestion((prev) => prev + 1);
            } else {
                soundEffects.background.stop();
                if (soundFxOn) soundEffects.result.play();
                confetti({
                    particleCount: 200,
                    spread: 100,
                    origin: { y: 0.5 },
                });
                setShowResult(true);
            }
        }, 2000);
    };
    const restartGame = () => {
        setCurrentQuestion(0);
        setScore(0);
        setShowResult(false);
        setFeedback(null);
        setBgClass("techquiz-primary-bg");
        setCountdown(3);
        setShowStart(true);
        setIsOptionDisabled(false);
        if (bgMusicOn) soundEffects.background.play();
    };
    const startGame = () => {
        setGameStarted(true);
        setShowStart(true);
        setCountdown(3);
        if (bgMusicOn) soundEffects.background.play();
    };
    return (
        <div className={`min-vh-100 ${bgClass}`}>
            <AnimatePresence>
                {!gameStarted ? (
                    <motion.div
                        key="start-screen"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="d-flex flex-column justify-content-center align-items-center min-vh-100"
                    >
                        <h2 className="display-3 display-md-1 fw-bold text-center mb-5 px-3 text-white">
                            Welcome to the Tech Quiz!
                        </h2>
                        <button
                            onClick={startGame}
                            className="techquiz-start-btn green"
                        >
                            ▶️ Start Game
                        </button>
                    </motion.div>
                ) : showStart ? (
                    <motion.div
                        key="countdown"
                        initial={{ opacity: 0, scale: 0.3 }}
                        animate={{ opacity: 1, scale: 1.2 }}
                        exit={{ opacity: 0, scale: 0.3 }}
                        transition={{ duration: 0.6 }}
                        className="display-1 fw-bolder d-flex justify-content-center align-items-center min-vh-100"
                    >
                        {countdown === 0 ? "🎮 GO!" : countdown}
                    </motion.div>
                ) : (
                    <div className="container py-4 py-lg-5">
                        <header className="d-flex justify-content-between align-items-center mb-4 mb-lg-5">
                             <Link to='/quize' className="btn">Back</Link>
                            <div className="techquiz-progress fs-5 fs-md-4 py-2 px-4">
                               
                                <p className="m-0">
                                    {" "}
                                    Question {currentQuestion + 1}/
                                    {questions.length}
                                </p>
                            </div>

                            <div className="d-flex gap-2">
                                <button
                                    onClick={() => setBgMusicOn(!bgMusicOn)}
                                    className="d-flex flex-column align-items-center bg-white rounded-3 p-1 p-sm-2 border-0"
                                >
                                    <span className="fs-5 fs-sm-1">
                                        {bgMusicOn ? "🎵" : "🔇"}
                                    </span>
                                    <small className="fs-9 fs-sm-2">
                                        {bgMusicOn ? "Music On" : "Music Off"}
                                    </small>
                                </button>
                                <button
                                    onClick={() => setSoundFxOn(!soundFxOn)}
                                    className="d-flex flex-column align-items-center bg-white rounded-3 p-1 p-sm-2 border-0"
                                >
                                    <span className="fs-5 fs-sm-1">
                                        {soundFxOn ? "🔊" : "🔕"}
                                    </span>
                                    <small className="fs-9 fs-sm-2">
                                        {soundFxOn ? "Sound On" : "Sound Off"}
                                    </small>
                                </button>
                            </div>
                        </header>
                        <AnimatePresence mode="wait">
                            {!showResult ? (
                                <motion.div
                                    key={`question-${currentQuestion}`}
                                    initial={{ opacity: 0, y: 60 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -60 }}
                                    transition={{ duration: 0.6 }}
                                >
                                    <motion.h5
                                        className="display-5 display-lg-3 text-center mb-4 mb-lg-5 fw-bolder px-2 text-white"
                                        initial={{ scale: 0.8 }}
                                        animate={{ scale: 1 }}
                                    >
                                        {questions[currentQuestion].question}
                                    </motion.h5>
                                    <div className="row g-3 g-lg-4 justify-content-center">
                                        {questions[currentQuestion].options.map(
                                            (option, index) => (
                                                <div
                                                    className="col-12 col-md-10 col-lg-6"
                                                    key={index}
                                                >
                                                    <motion.button
                                                        onClick={() =>
                                                            handleAnswer(option)
                                                        }
                                                        whileHover={{
                                                            scale: 1.05,
                                                        }}
                                                        whileTap={{
                                                            scale: 0.95,
                                                        }}
                                                        className="techquiz-option-btn w-100"
                                                        disabled={
                                                            isOptionDisabled
                                                        }
                                                    >
                                                        <div className="d-flex align-items-center justify-content-center gap-3">
                                                            <div>
                                                                <div className="techquiz-option-number mb-2">
                                                                    {index + 1}
                                                                </div>
                                                                <div className="text-break text-dark">
                                                                    {option}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </motion.button>
                                                </div>
                                            )
                                        )}
                                    </div>
                                    {feedback && (
                                        <motion.div
                                            className="display-6 text-center mt-4 mt-lg-5 fw-bold"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                        >
                                            {feedback}
                                        </motion.div>
                                    )}
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="results"
                                    initial={{ opacity: 0, scale: 0.7 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="card border-0 bg-dark bg-opacity-25 p-4 p-lg-5 mx-auto"
                                    style={{
                                        backdropFilter: "blur(10px)",
                                        maxWidth: "800px",
                                    }}
                                >
                                    <h2 className="display-6 display-lg-4 text-center mb-4 text-white">
                                        🏁 <b>Quiz Complete!</b>
                                    </h2>
                                    <p className="fs-3 fs-lg-2 text-center mb-4">
                                        🌟 Score: {score} / {questions.length}
                                    </p>
                                    <button
                                        onClick={restartGame}
                                        className="btn btn-light fs-5 fs-lg-4 py-2 px-4 mx-auto d-block"
                                    >
                                        🔄 Play Again
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                        {gameStarted && !showStart && !showResult && (
                            <div className="position-fixed bottom-0 start-0 m-3 bg-white px-4 py-2 rounded shadow fw-bold z-3">
                                🌟 Score: {score}
                            </div>
                        )}
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};
export default QuizeGame;
